import {
	enUS,
	cs,
	da,
	es,
	enGB,
	de,
	fr,
	id,
	it,
	hu,
	ms,
	nl,
	nb,
	pl,
	pt,
	ro,
	sk,
	fi,
	sv,
	vi,
	he,
	ar,
	zhCN,
	zhTW,
	ja,
} from 'date-fns/locale';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import pino from 'pino';

import type { Settlement } from '../queries/settlementQuery';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

const logger = pino();

const TsLocaleToDateFnsLocale = {
	cs,
	da,
	es,
	'en-US': enUS,
	en_US: enUS,
	'en-GB': enGB,
	de,
	fr,
	id,
	it,
	hu,
	ms,
	nl,
	nb,
	pl,
	pt,
	'pt-PT': 'pt-PT',
	ro,
	sk,
	fi,
	sv,
	'vi-VN': vi,
	'he-IL': he,
	'ar-SA': ar,
	zh: zhCN,
	'zh-TW': zhTW,
	ja,
};

export type SortBy = 'most-recent' | 'oldest';

export const getDateFnsLocale = (language: string) => TsLocaleToDateFnsLocale[language] ?? enUS;

function isValidLocale(locale: string): boolean {
	try {
		const formattedLocale = locale.replaceAll('_', '-');
		Intl.getCanonicalLocales(formattedLocale);
		return true;
	} catch {
		return false;
	}
}

const DATE_FORMATS = [
	'YYYY-MM-DD',
	'DD-MM-YYYY',
	'MM/DD/YYYY',
	'DD/MM/YYYY',
	'YYYY/MM/DD',
	'DD.MM.YYYY',
	'YYYY.MM.DD',
	'MMM DD, YYYY',
	'DD MMM YYYY',
	'YYYY-MM-DDTHH:mm:ss.SSSZ',
];

export function formatDate(date: string | number | Date | dayjs.Dayjs, locale = 'en-US', timezoneId = 'UTC'): string {
	try {
		let dateObj: Date;

		if (typeof date === 'string') {
			let parsedDate = dayjs(date).tz(timezoneId, true);

			if (!parsedDate.isValid()) {
				for (const format of DATE_FORMATS) {
					parsedDate = dayjs(date, format).tz(timezoneId, true);
					if (parsedDate.isValid()) {
						break;
					}
				}
			}

			if (!parsedDate.isValid()) {
				logger.error({ message: `Invalid date string format: ${date}` });
				return date;
			}
			dateObj = parsedDate.toDate();
		} else if (typeof date === 'number') {
			const parsedDate = dayjs(date).tz(timezoneId, true);
			if (!parsedDate.isValid()) {
				logger.error({ message: `Invalid timestamp: ${date}` });
			}
			dateObj = parsedDate.toDate();
		} else if (date instanceof Date) {
			dateObj = dayjs(date).tz(timezoneId, true).toDate();
		} else if (dayjs.isDayjs(date)) {
			dateObj = date.tz(timezoneId, true).toDate();
		} else {
			logger.error({ message: `Unsupported date format: ${typeof date}` });
			return date;
		}

		const validLocale = isValidLocale(locale) ? locale.replaceAll('_', '-') : 'en-US';

		const options: Intl.DateTimeFormatOptions = {
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
			timeZone: timezoneId,
		};

		let formattedDate = new Intl.DateTimeFormat(validLocale, options).format(dateObj);

		return formattedDate;
	} catch (error) {
		logger.error({ message: `Error formatting date: ${date}`, error });
		return String(date);
	}
}

export const compareSettlementDates = (a: Settlement, b: Settlement, sortBy: SortBy) => {
	const dateA = new Date(a.date ?? '').getTime();
	const dateB = new Date(b.date ?? '').getTime();
	return sortBy === 'most-recent' ? dateB - dateA : dateA - dateB;
};
